import { Component, computed, inject } from "@angular/core";
import { SharedModule } from "../../../../shared/shared.module";
import { ViewerSettingsService } from "../viewer-settings.store";
import { NzModalRef } from "ng-zorro-antd/modal";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { JobStateService } from "../job-state.store";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { firstValueFrom } from "rxjs";
import { allSliceProcessings } from "../../model/slice_processings";

@Component({
  selector: "app-min-max-dialog",
  imports: [SharedModule],
  templateUrl: "./min-max-dialog.component.html",
  styleUrl: "./min-max-dialog.component.less",
})
export class MinMaxDialogComponent {
  private _fb = inject(FormBuilder);
  protected viewerSettings = inject(ViewerSettingsService);
  
  private cachedProjectService = inject(CachedProjectService)
  private jobStateSettings = inject(JobStateService);
  protected projectDefaults: {min: number, max: number} = null
  protected zeroCenteredModel = computed(() =>
    
    allSliceProcessings[this.jobStateSettings
      .selectedModel()]?.includes("_zero_centred_params_process"),
  );
  protected is2D = computed(() => this.jobStateSettings.jobData().modelGrid.is2d)

  readonly #modal = inject(NzModalRef);

  minMaxForm: FormGroup;

  constructor() {
    firstValueFrom(this.cachedProjectService.currentProject)
      .then(
        (project) =>
          (this.projectDefaults =
            project.displayVariables[
              this.jobStateSettings?.selectedModel()
            ] ?? null),
      )
      .catch(() => (this.projectDefaults = null));

    this.minMaxForm = this._fb.group({
      minValue: [null, Validators.required],
      maxValue: [null, Validators.required],
      auto: [null, Validators.required],
      depthMinValue: [null], // dynamically add validators below
      depthMaxValue: [null], // dynamically add validators below
    });

    // if is 2D, we can remove or add the Validation on the depthMin depthMax
    if (this.is2D() || !this.viewerSettings.showDepthSlice()){
      this.minMaxForm.get('depthMinValue').removeValidators(Validators.required)
      this.minMaxForm.get('depthMaxValue').removeValidators(Validators.required)
    }else{
      this.minMaxForm.get('depthMinValue').addValidators(Validators.required)
      this.minMaxForm.get('depthMaxValue').addValidators(Validators.required)
    }

    this.minMaxForm
      .get("auto")
      .valueChanges.pipe(takeUntilDestroyed())
      .subscribe({
        next: (value) => {
          // is the user is setting auto true, the value should be set to the 'min' and 'max' of the inline slice
          // the inputs should also be made disabled
          this.viewerSettings.auto.set(value);
          if (value) {
            // just apply the inline true min max
            this.minMaxForm.patchValue({
              minValue: this.viewerSettings.trueMin().inline,
              maxValue: this.viewerSettings.trueMax().inline,
              depthMinValue: this.viewerSettings.trueMin().depth,
              depthMaxValue: this.viewerSettings.trueMax().depth,
            });

            // case of auto is true
            this.minMaxForm.get("minValue").disable();
            this.minMaxForm.get("maxValue").disable();

            this.minMaxForm.get("depthMinValue").disable();
            this.minMaxForm.get("depthMaxValue").disable();
          } else {
            // case of auto is false
            this.minMaxForm.get("minValue").enable();
            this.minMaxForm.get("maxValue").enable();

            this.minMaxForm.get("depthMinValue").enable();
            this.minMaxForm.get("depthMaxValue").enable();
          }
        },
      });

    // if it is a zero centered model type, then we add one additional binding of the maxValue updating the minValue with its negation
    if (this.zeroCenteredModel()) {
      this.minMaxForm 
        .get("maxValue")
        .valueChanges.pipe(takeUntilDestroyed())
        .subscribe({
          next: (value) => this.minMaxForm.patchValue({ minValue: -value }),
        });
      this.minMaxForm
        .get("depthMaxValue")
        .valueChanges.pipe(takeUntilDestroyed())
        .subscribe({
          next: (value) =>
            this.minMaxForm.patchValue({ depthMinValue: -value }),
        });
    }
  }

  ngOnInit() {
    this.minMaxForm.setValue({
      minValue:
        this.viewerSettings.inlineMinMax().min ??
        this.viewerSettings.trueMin().inline,
      maxValue:
        this.viewerSettings.inlineMinMax().max ??
        this.viewerSettings.trueMax().inline,
      auto: this.viewerSettings.auto() ?? false,
      depthMinValue:
        this.viewerSettings.depthMinMax().min ??
        this.viewerSettings.trueMin().depth,
      depthMaxValue:
        this.viewerSettings.depthMinMax().max ??
        this.viewerSettings.trueMax().depth,
    });
  }

  handleCancel() {
    this.#modal.destroy();
  }

  async useProjectDefualts(){
    this.minMaxForm.patchValue({
      auto: false,
      minValue: this.projectDefaults.min,
      maxValue: this.projectDefaults.max,
      depthMinValue: this.projectDefaults.min,
      depthMaxValue: this.projectDefaults.max,
    })
  }

  handleOk() {
    if (!this.minMaxForm.valid) return;
    let values = this.minMaxForm.getRawValue();
    // set the min max. inline, crossline, depth is to be seen now
    // if the values are set to null, that will be null,
    // if the values are set to auto, the ones for inline shall be used for all
    // if a certain value is set, that will be used for all
    // so in essence, whatever value is set here, should be used for all

    this.viewerSettings.inlineMinMax.set({
      min: values.minValue,
      max: values.maxValue,
    });

    if (this.viewerSettings.showDepthSlice()) {
      this.viewerSettings.depthMinMax.set({
        min: values.depthMinValue,
        max: values.depthMaxValue,
      });
    }

    this.#modal.destroy();
  }
}
